import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Compass, AlertTriangle, Activity, ListChecks, Sparkles,
  LayoutGrid, BarChart3, Lightbulb, ClipboardList, Building2, Loader2, Contact,
  FileText, TrendingUp, ChevronRight, CheckCircle2,
} from "lucide-react";

const ATTENTION_ICON = {
  setup: Building2, task: ListChecks, content: LayoutGrid,
  idea: Lightbulb, sop: ClipboardList, status: Building2, planning: Compass, crm: Contact,
};
const FEED_ICON = { content: LayoutGrid, idea: Lightbulb, sop: ClipboardList, business: BarChart3 };

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function SectionTitle({ icon: Icon, children, testid }) {
  return (
    <div className="flex items-center gap-2 mb-4" data-testid={testid}>
      <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
      <h2 className="font-heading text-xl text-foreground">{children}</h2>
    </div>
  );
}

function EmptyHint({ text, cta, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full text-left rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors flex items-center justify-between">
      <span>{text}</span>
      <span className="inline-flex items-center gap-1 text-primary font-medium shrink-0">{cta} <ChevronRight className="h-4 w-4" /></span>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/dashboard")
      .then((r) => { if (active) { setData(r.data); setError(false); } })
      .catch(() => { if (active) setError(true); });
    load();
    window.addEventListener("sfos:refresh", load);
    return () => { active = false; window.removeEventListener("sfos:refresh", load); };
  }, []);

  if (error) {
    return (
      <div className="max-w-md mx-auto text-center py-24" data-testid="dashboard-error">
        <p className="text-muted-foreground mb-4">We couldn't load your dashboard just now.</p>
        <Button onClick={() => window.location.reload()} className="rounded-full">Try again</Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="dashboard-loading">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const name = data.owner_name || (user?.name || "").split(" ")[0] || "there";
  const rec = data.recommended;
  const attention = data.needs_attention || [];
  const recent = data.recent_work || [];
  const priorities = data.priorities || [];
  const active = data.active_work || {};
  const insights = data.insights;
  const go = (link) => navigate(link);

  const QUICK = [
    { label: "Continue Content", icon: LayoutGrid, link: "/content", testid: "quick-continue-content" },
    { label: "Review Business Insights", icon: BarChart3, link: "/business", testid: "quick-business-insights" },
    { label: "Add an Idea", icon: Lightbulb, link: "/ideas", testid: "quick-action-add-idea" },
    { label: "Review SOPs", icon: ClipboardList, link: "/sops", testid: "quick-review-sops" },
    { label: "Update Business Information", icon: Building2, link: "/setup", testid: "quick-business-info" },
  ];

  return (
    <div className="space-y-8" data-testid="business-dashboard">
      {/* Greeting */}
      <div data-testid="dashboard-greeting">
        <p className="font-accent text-primary text-lg">{greeting()}, {name}.</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground mt-1">
          {data.business_name ? data.business_name : "Your business"} at a glance
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* Recommended Next Step */}
      {rec && (
        <Card className="rounded-3xl border-none shadow-soft-lg bg-primary text-primary-foreground p-7 sm:p-8" data-testid="recommended-next-step">
          <div className="flex items-center gap-2 text-primary-foreground/80 text-xs font-semibold uppercase tracking-wide mb-3">
            <Sparkles className="h-4 w-4" /> What to work on next
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl leading-tight">{rec.title}</h2>
          <p className="text-primary-foreground/85 mt-3 leading-relaxed max-w-2xl">{rec.reason}</p>
          <Button onClick={() => go(rec.link)} data-testid="recommended-cta"
            className="mt-6 rounded-full h-11 px-6 bg-card text-primary hover:bg-card/90 gap-2">
            {rec.cta} <ArrowRight className="h-4 w-4" />
          </Button>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Business Snapshot */}
          <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-7" data-testid="business-snapshot">
            <SectionTitle icon={Compass} testid="snapshot-title">Where your business stands</SectionTitle>

            {/* Priorities */}
            <div className="mb-5" data-testid="snapshot-priorities">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">This week's focus</p>
              {data.primary_focus && <p className="text-foreground font-medium mb-2">{data.primary_focus}</p>}
              {priorities.length > 0 ? (
                <ul className="space-y-1.5">
                  {priorities.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{p}
                    </li>
                  ))}
                </ul>
              ) : !data.primary_focus ? (
                <EmptyHint text="No priorities set yet." cta="Plan the week" onClick={() => go("/planning")} testid="snapshot-priorities-empty" />
              ) : null}
            </div>

            {/* Active work */}
            <div className="mb-5" data-testid="snapshot-active-work">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">In progress</p>
              {(active.in_progress_weeks || []).length > 0 ? (
                <div className="space-y-2">
                  {active.in_progress_weeks.map((w) => (
                    <button key={w.id} onClick={() => go("/content")} data-testid="active-week-item"
                      className="w-full text-left rounded-2xl border border-border px-4 py-3 hover:bg-muted/50 transition-colors flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate">{w.title}</p>
                        <p className="text-xs text-muted-foreground">{w.completed}/{w.total} formats complete{w.drafts ? ` · ${w.drafts} in draft` : ""}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <EmptyHint text="Nothing in progress right now." cta="Start a content week" onClick={() => go("/content")} testid="snapshot-active-empty" />
              )}
              {(active.new_ideas > 0) && (
                <p className="text-sm text-muted-foreground mt-2">
                  {active.new_ideas} idea{active.new_ideas > 1 ? "s" : ""} captured, waiting to become content.
                </p>
              )}
            </div>

            {/* Insights */}
            <div data-testid="snapshot-insights">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Business insights</p>
              {insights ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-2xl bg-muted/40 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Revenue ({insights.month})</p>
                    <p className="font-heading text-lg text-foreground">${insights.revenue.toLocaleString()}</p>
                  </div>
                  <div className="rounded-2xl bg-muted/40 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Expenses</p>
                    <p className="font-heading text-lg text-foreground">${insights.expenses.toLocaleString()}</p>
                  </div>
                  <div className="rounded-2xl bg-muted/40 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Net</p>
                    <p className={`font-heading text-lg ${insights.net >= 0 ? "text-primary" : "text-destructive"}`}>${insights.net.toLocaleString()}</p>
                  </div>
                  <div className="rounded-2xl bg-muted/40 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Goal progress</p>
                    <p className="font-heading text-lg text-foreground">{insights.goal_pct != null ? `${insights.goal_pct}%` : "—"}</p>
                  </div>
                </div>
              ) : (
                <EmptyHint text="No business numbers recorded yet." cta="Open Business Center" onClick={() => go("/business")} testid="snapshot-insights-empty" />
              )}
            </div>
          </Card>

          {/* Recent Work */}
          <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-7" data-testid="recent-work">
            <SectionTitle icon={Activity} testid="recent-work-title">What you've been working on</SectionTitle>
            {recent.length > 0 ? (
              <div className="divide-y divide-border">
                {recent.map((item, i) => {
                  const Icon = FEED_ICON[item.type] || FileText;
                  return (
                    <button key={i} onClick={() => go(item.link)} data-testid="recent-work-item"
                      className="w-full flex items-center gap-3 py-3 text-left hover:bg-muted/40 -mx-2 px-2 rounded-xl transition-colors">
                      <span className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4 text-primary" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground truncate"><span className="text-muted-foreground">{item.action}</span> {item.title}</p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{timeAgo(item.ts)}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <EmptyHint text="No recent activity yet." cta="Create your first content" onClick={() => go("/content")} testid="recent-work-empty" />
            )}
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Needs Attention */}
          <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-7" data-testid="needs-attention">
            <SectionTitle icon={AlertTriangle} testid="needs-attention-title">Needs your attention</SectionTitle>
            {attention.length > 0 ? (
              <div className="space-y-2">
                {attention.map((a) => {
                  const Icon = ATTENTION_ICON[a.type] || AlertTriangle;
                  return (
                    <button key={a.id} onClick={() => go(a.link)} data-testid={`attention-${a.id}`}
                      className="w-full text-left rounded-2xl border border-border px-4 py-3 hover:bg-muted/50 transition-colors flex items-start gap-3">
                      <span className="h-8 w-8 rounded-lg bg-accent/40 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="h-4 w-4 text-secondary" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{a.label}</p>
                        {a.detail && <p className="text-xs text-muted-foreground truncate">{a.detail}</p>}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl bg-muted/40 px-4 py-6 text-center" data-testid="attention-clear">
                <CheckCircle2 className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">You're all caught up. Nothing needs attention right now.</p>
              </div>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-7" data-testid="quick-actions">
            <SectionTitle icon={TrendingUp} testid="quick-actions-title">Quick actions</SectionTitle>
            <div className="space-y-2">
              {QUICK.map((q) => {
                const Icon = q.icon;
                return (
                  <button key={q.testid} onClick={() => go(q.link)} data-testid={q.testid}
                    className="w-full flex items-center gap-3 rounded-2xl border border-border px-4 py-3 hover:bg-muted/50 hover:border-primary/30 transition-colors text-left">
                    <Icon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground flex-1">{q.label}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
